import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select'
import { getProjectQuoteCategoriesV2 } from '@/components/features/project-detail/quotes/quotes-v2.api'
import type { ProjectCategoryDto } from '@/components/features/project-detail/quotes/types'
import { createSettingsCategory } from '@/lib/api/v2/settings'
import { useAuth } from '@/lib/auth-context'
import { PERMISSIONS } from '@/lib/permissions'
import { showErrorToast } from '@/lib/toast'
import { ApiError } from '@/lib/api/client'
import { categoryOptions, choiceOf, NEW_VALUE, selectValueOf, type CategoryChoice } from './category-choice'

interface OrganizationCategorySelectProps {
  projectId: string
  id?: string
  value: CategoryChoice
  onChange: (value: CategoryChoice) => void
}

/**
 * Files a hand-typed line under one of the organisation's categories (SOLY-IMPORT-003 S07b).
 * Someone who manages settings can add a category on the spot; others pick from the list.
 */
export function OrganizationCategorySelect({ projectId, id, value, onChange }: OrganizationCategorySelectProps) {
  const { user } = useAuth()
  const canCreate = user?.permissions?.includes(PERMISSIONS.SETTINGS_MANAGE) === true
  const [categories, setCategories] = useState<ProjectCategoryDto[]>([])
  const [isCreating, setIsCreating] = useState(false)
  const [newLabel, setNewLabel] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    getProjectQuoteCategoriesV2(projectId)
      .then((res) => {
        if (!cancelled) setCategories(res.categories ?? [])
      })
      .catch(() => {
        if (!cancelled) setCategories([])
      })
    return () => {
      cancelled = true
    }
  }, [projectId])

  const handleSelect = (selected: string) => {
    if (selected === NEW_VALUE) {
      setIsCreating(true)
      return
    }
    onChange(choiceOf(selected, categories, value))
  }

  const handleCreate = async () => {
    const label = newLabel.trim()
    if (!label) return
    setIsSaving(true)
    try {
      const created = await createSettingsCategory({ label, translations: {} })
      setCategories((prev) => [...prev, { id: created.id, label: created.label, isHidden: false }])
      onChange({ categoryId: created.id, categoryLabel: created.label })
      setIsCreating(false)
      setNewLabel('')
    } catch (e) {
      showErrorToast(e instanceof ApiError && e.message ? e.message : 'Impossible de créer la catégorie.')
    } finally {
      setIsSaving(false)
    }
  }

  if (isCreating) {
    return (
      <div className="flex gap-2">
        <Input
          id={id}
          autoFocus
          value={newLabel}
          maxLength={256}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleCreate()
            }
          }}
          placeholder="Nom de la nouvelle catégorie"
          data-testid="category-new-label-input"
        />
        <Button type="button" size="sm" onClick={handleCreate} disabled={isSaving || !newLabel.trim()} data-testid="category-new-save-btn">
          Créer
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setIsCreating(false)} data-testid="category-new-cancel-btn">
          Annuler
        </Button>
      </div>
    )
  }

  return (
    <Select value={selectValueOf(value)} onValueChange={handleSelect}>
      <SelectTrigger id={id} data-testid="category-select">
        <SelectValue placeholder="Aucune" />
      </SelectTrigger>
      <SelectContent>
        {categoryOptions(categories, value).map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
        {canCreate && (
          <>
            <SelectSeparator />
            <SelectItem value={NEW_VALUE}>Nouvelle catégorie…</SelectItem>
          </>
        )}
      </SelectContent>
    </Select>
  )
}
